import React, { useEffect, useState, useRef } from 'react';
import {useDispatch, useSelector} from "react-redux";

import {getDataColor, addAlpha, getCustomMatchDataColor} from "../../../../utilities/ColorUtilities";

import {findComponent} from "../../../../redux/page/pageActions";
import SettingsForm from "../settings/SettingsForm";
import {appSetModalContent} from "../../../../redux/app/appActions";

import ClearFilterBtn from "../../../application/ClearFilterBtn";
import {useTranslation} from "react-i18next";

import Highcharts, {theme} from 'highcharts/highstock';

import { styled, Card, CardHeader, CardContent, LinearProgress } from '@mui/material';
import {areLoadDependenciesMet, getFieldAlias, getFieldFormatters} from "../../../../utilities/DataSourceUtilities";
import * as DataUtilities from "../../../../utilities/DataUtilities";
import useDataSource from "../../../../hooks/useDataSource";
import useMainCanvasSize from "../../../../hooks/useMainCanvasSize";
import useWindowSize from "../../../../hooks/useWindowSize";
import {formatDate, formatNumber, formatStyle, formatSuffix} from "../../../../utilities/FormatUtilities";
import {dataAddFilter, dataRemoveFilter, filterUpdateConditionAction} from "../../../../redux/data/dataActions";


const StyledDiv = styled('div')(({ theme}) => ({
	width: '100%',
	height: '100%'
}));

const StyledCardHeader = styled(CardHeader)({
	padding: '8px 8px 0px 8px',
	alignItems: 'start',
	textAlign: 'left',
	textTransform: 'none',
});

export default function ScatterChart({id, configuration, xId}) {
	const dispatch = useDispatch();
	const { t } = useTranslation();

	// App Settings
	const filters = useSelector(state => state.data.filters);
	const persist = useSelector(state => state.persist);
	const pageID = useSelector(state => state.app.pageID);
	const editXId = useSelector(state => state.page.componentEdit.xId);
	const componentUpdated = useSelector(state => state.page.componentUpdated);
	const componentWrapperEdit = useSelector(state => state.page.componentWrapperEdit);
	const pages = useSelector(state => state.page.pages);
	const pageConfigs = useSelector(state => state.config.data.pageConfigs);
	const activeRole = useSelector(state => state.persist.activeRoleName);

	// Component State
	const [compConfig, setCompConfig] = useState(configuration);

	const [title, setTitle] = useState(compConfig.name?.title ? compConfig.name.title : '');
	const [subtitle, setSubTitle] = useState(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	const [chart, setChart] = useState(null);
	const [chartData, setChartData] = useState(null);
	const [sliceValue, setSliceValue] = useState(-2112);
	const [sliceGroupedValue, setSliceGroupedValue] = useState(-2112);
	const [sliceValueNotFound, setSliceValueNotFound] = useState(false);
	const [noDataMessage, setNoDataMessage] = useState("No Data");
	// const [categoryFieldAlias, setCategoryFieldAlias] = useState(null);
	const [categoryFieldFormatters, setCategoryFieldFormatters] = useState(null);
	const [xValueFormatters, setXValueFormatters] = useState(null);
	const [yValueFormatters, setYValueFormatters] = useState(null);
	const [countFieldAlias, setCountFieldAlias] = useState(null);
	const [countFieldFormatters, setCountFieldFormatters] = useState(null);
	const [valueFieldAlias, setValueFieldAlias] = useState(null);
	const [valueFieldFormatters, setValueFieldFormatters] = useState(null);
	const [showProgress, setShowProgress] = useState(compConfig.element?.showFetching !== undefined ? compConfig.element?.showFetching : true);
	const [cardHeaderStyle, setCardHeaderStyle] = useState({});
	const [cardContentStyle, setCardContentStyle] = useState({});
	const [resizedComponent, setResizedComponent] = useState(false);


	const highchartOptions = compConfig?.highchartOptions ? compConfig?.highchartOptions : {};
	const dataSourceName = compConfig.dataSource?.name ? compConfig.dataSource?.name : '';
	const dependencies = compConfig.dataSource?.dependencies ? compConfig.dataSource?.dependencies : null;
	const xValueField = compConfig.dataSource?.fields?.xValueField ? compConfig.dataSource?.fields?.xValueField : '';
	const yValueField = compConfig.dataSource?.fields?.yValueField ? compConfig.dataSource?.fields?.yValueField : '';
	const condition = compConfig.dataSource?.condition ? compConfig.dataSource?.condition : null;
	const customColors = compConfig?.element?.colors;
	const styles = compConfig?.element?.styles;
	const filterField = compConfig.xFilter?.field ? compConfig.xFilter?.field : null;
	const filterByValue = compConfig.xFilter?.byValue !== undefined ? compConfig.xFilter?.byValue : false;
	const filterOperator = compConfig.xFilter?.operator ? compConfig.xFilter?.operator : null;
	let chartSeries = compConfig.chartSeries ? compConfig.chartSeries : [];
	const chartSeriesDynamic = compConfig?.chartSeriesDynamic !== undefined ? compConfig.chartSeriesDynamic : false;

	const chartRef = useRef(null);
	const dataSource = useDataSource(dataSourceName);
	const size = useMainCanvasSize();
	const windowSize = useWindowSize();

	useEffect(() => {

		return () => { if (chart) chart.destroy() }
	}, []);

	// Did Component Wrapper Trigger Redux State Change?
	useEffect(() => {
		if (componentWrapperEdit.parentId && componentWrapperEdit.childId && xId) {
			if ((componentWrapperEdit.parentId === xId) || (componentWrapperEdit.childId === xId)) {
				const page = pages.find(page => page.name === pageID);
				for (let i = 0; i < pageConfigs.length; i++ ) {
					const roleConfig = page.roles.find(r => r.name === activeRole)[pageConfigs[i]];
					if (roleConfig) {
						const findResult = findComponent(xId, componentWrapperEdit.childId === xId ? componentWrapperEdit.parentId : null, roleConfig.configuration.components);
						if (findResult) {
							setCompConfig(findResult.configuration);
							break;
						}
					}
				}
			}
		}
	},[componentWrapperEdit]);

	// Component Configuration (compConfig) changed, reset state
	useEffect(() => {
		setTitle(compConfig.name?.title ? compConfig.name.title : '');
		setSubTitle(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	},[compConfig]);

	useEffect(() => {
		resizeCard();
	},[title, subtitle]);

	// componentUpdated & Resized
	useEffect(() => {
		if (xId) {
			if (componentUpdated.id === xId) {
				setCompConfig(componentUpdated.config);
				setResizedComponent(true);
			}
		}
	}, [componentUpdated]);

	// xEdit
	useEffect(() => {
		if (editXId) {
			if ((editXId === xId) && (pageID)) {

				const placeholders = {
					title: title,
					subtitle: subtitle
				}

				const settingsConfig = {
					title: 'Scatter Chart Configuration',
					parent: {
						minWidth: '400px',
						maxWidth: '400px',
						minHeight: '520px',
						maxHeight: '520px'
					},
					paper: null,
					button: null,
					content: <SettingsForm pageID={pageID} xId={xId} configuration={compConfig} schemaType={'ScatterCart'} placeholders={placeholders}/>,
					tooltipContent: null
				}
				dispatch(appSetModalContent(settingsConfig));
			}
		}
	},[editXId, pageID]);

	useEffect(() => {

		// console.log('-----------------SCATTER CHART---------STATUS->');
		// console.log('fetching: {0}'.format(dataSource.fetching));
		// console.log('fetched: {0}'.format(dataSource.fetched));
		// console.log('error: {0}'.format(dataSource.error));

		if (dataSource.error) {
			setNoDataMessage("Error");
			setChartData([]);
		}
		else if (dataSource.data && dataSource.fetched) {

			// If our dependencies are not met then we're
			// loading/no chance until they are
			if (!areLoadDependenciesMet(dependencies, filters)) {
				return;
			}

			setNoDataMessage("No Data");

			// Setup base data as filtered results or a baseline if included
			let data =[];
			if (condition) {
				let conditionValue;
				switch (condition.conditionType.toLowerCase()) {
					case 'group':
						conditionValue = condition.conditionValue;
						break;
					default:
						conditionValue = condition.conditionValue;
						break;
				}
				const newData = DataUtilities.filterDataSet(condition.conditionType, condition.conditionOperator, condition.conditionField, conditionValue, dataSource.data, null);
				// If condition found, push, otherwise, keep null/empty
				if (newData !== undefined) {
					data = newData;
				}
			} else {
				data = dataSource.data;
			}
			// if (dataSource.includeBaselineResults && dataSource.baselines.length > 0) {
			// 	// Find my baseline
			// 	const baseline = dataSource.baselines.find(d => {
			// 		return d.id === id
			// 	});
			//
			// 	// console.log('------------BASELINE------------->', baseline);
			// 	if (baseline) {
			// 		let useBaseline = true;
			//
			// 		// If there is a baseline then we use
			// 		// that value IF we can highlight
			// 		if (sliceValue !== -2112) {
			//
			// 			if (!baseline.result.find(d => {
			// 					if (d[categoryField] && sliceValue) {
			// 						return d[categoryField].toString().toLowerCase() === sliceValue.toString().toLowerCase();
			// 					} else {
			// 						return false;
			// 					}
			// 				}
			// 			)
			// 			) {
			// 				useBaseline = false;
			// 			}
			// 		}
			//
			// 		// console.log('------------------USE BASELINE---------------->', useBaseline);
			// 		if (useBaseline) {
			// 			data = baseline.result;
			// 		}
			// 	}
			// }

			// Rehydrate slice
			// const componentFilters = filters.find(f => {
			// 	return f.id === id // && f.field === categoryField
			// });
			//
			// if (componentFilters) {
			// 	// TODO: Add utility or store in state since this
			// 	// only assumes single condition - for now we'll use
			// 	// local context however sharing across components
			// 	// TBD will share same f.id
			// 	// HACK:
			// 	let condition = componentFilters.conditions[0];
			// 	if (condition.indexOf("eq '") === 0) {
			// 		// String eq
			// 		condition = condition.substring(condition.indexOf("eq '") + 4);
			// 		condition = condition.substring(0, condition.length - 1);
			//
			// 		setSliceValue(condition);
			// 	} else if (condition.indexOf("eq ") === 0) {
			// 		// Numeric eq
			// 		condition = condition.substring(condition.indexOf("eq ") + 3);
			//
			// 		setSliceValue(condition);
			// 	} else if (condition.indexOf("like '") === 0) {
			// 		// String like
			// 		condition = condition.substring(condition.indexOf("like '") + 6);
			// 		condition = condition.substring(0, condition.length - 1);
			//
			// 		if (condition.length > 2) {
			// 			if (condition.substring(0, 1) === '%') {
			// 				condition = condition.substring(1);
			// 			}
			// 			if (condition.substring(condition.length -1) === '%') {
			// 				condition = condition.substring(0, condition.length -1);
			// 			}
			// 		}
			//
			// 		setSliceValue(condition);
			// 	} else {
			// 		console.log("**WARN** ScatterChart '{0}' was not a recognized filter condition".format(condition));
			// 	}
			// }

			// Formatters - using for tooltips
			// setCategoryFieldFormatters(getFieldFormatters(dataSource, categoryField));
			setXValueFormatters(getFieldFormatters(dataSource, xValueField));
			setYValueFormatters(getFieldFormatters(dataSource, yValueField));

			// Update Data/Chart
			setChartData(data);
		}
	}, [dataSource.data]);

	useEffect(() => {

		updateChart();

	}, [chartData, sliceValue, persist.themeLight, t]);

	useEffect(() => {
		const componentFilters = filters.find(f => {
			return f.id === id // && f.field === categoryField
		});

		if (!componentFilters && sliceValue !== -2112) {
			setSliceValue(-2112);
		}
	}, [filters]);

	useEffect( () => {
		if (chart) {
			// setTimeout required to accurately obtain chart.renderTo.clientHeight and chart.renderTo.clientWidth to perform the reflow
			setTimeout(function() {
				// console.log('reflow');
				chart.reflow();
				setResizedComponent(resizedComponent === true ? false : false);
			}, 300)
		}
	}, [size, windowSize, resizedComponent, cardHeaderStyle, cardContentStyle])

	const formatTooltip = (formatters, label, input) => {

		// console.log('{0}'.format('--> ScatterChart: formatTooltip()'));
		// console.log('FORMATTERS: ', formatters);
		// console.log('LABEL: {0} ({1})'.format(label, typeof label));
		// console.log('INPUT: {0} ({1})'.format(input, typeof input));

		let formattedValue = input, style = "", tooltip;
		if (formatters) {
			for (const formatter of formatters) {
				if (formatter.type.toLowerCase() === 'style') {
					style = formatStyle(input, formatter.config, input, formatter.condition, true);
				} else if (formatter.type.toLowerCase() === 'number') {
					formattedValue = formatNumber(input, formatter.config);
				} else if (formatter.type.toLowerCase() === 'date') {
					formattedValue = formatDate(input, formatter.config);
				} else if (formatter.type.toLowerCase() === 'suffix') {
					formattedValue = formatSuffix(input, formatter.config);
				}
			}
		}

		if (label) {
			tooltip = "<tr><td style='text-align:left'><span style=" + style + ">" + formattedValue + "</td></tr>";
		} else {
			tooltip = "<tr><td colspan='2' style=" + style + ">" + formattedValue + "</td></tr>";
		}

		// console.log('TOOLTIP: {0} ({1})'.format(tooltip, typeof tooltip));
		return tooltip;
	}

	const clickEvent = (event) => {
		// NOTE: You can look up other values (assuming you have an ID) by
		// filtering the input array
		let filterFieldToAdd;
		let filterValueToAdd;

		if (!filterByValue) {
			filterFieldToAdd = categoryField;
			filterValueToAdd = event.point.category;
		} else {
			filterFieldToAdd = filterField;
			filterValueToAdd = event.point.options.y;
		}

		if (sliceValue.toString().toLowerCase() === filterValueToAdd.toString().toLowerCase()) {
			dispatch(dataRemoveFilter(id));
		}
		else {
			if (filterOperator.toLowerCase() === 'like') {
				dispatch(dataAddFilter(id, filterFieldToAdd, " like '%{0}%'".format(filterValueToAdd), filterUpdateConditionAction.REPLACE, true, false));
			} else {
				dispatch(dataAddFilter(id, filterFieldToAdd, " eq '{0}'".format(filterValueToAdd), filterUpdateConditionAction.REPLACE, true, false));
			}
		}
	}

	const updateChart = () => {

		const data = chartData;
		console.log(data);

		if (!chart && data === null)
			return;

		if (chart && data === null) {
			// No Data
			chart.update({
				series: [{
					name: 'No Data',
					data: []
				}]
			}, true, true);
			chart.showNoData();
			return;
		}

		// const sliceValueToCompare = sliceValue.toString().toLowerCase();

		// Setup series
		// const seriesData = [
		// 	{
		// 		name: 'Female',
		// 		color: 'rgba(223, 83, 83, .5)',
		// 		data: [[161.2, 51.6], [167.5, 59.0], [159.5, 49.2], [157.0, 63.0], [155.8, 53.6],
		// 			[170.0, 59.0], [159.1, 47.6], [166.0, 69.8], [176.2, 66.8], [160.2, 75.2],
		// 			[172.5, 55.2], [170.9, 54.2], [172.9, 62.5], [153.4, 42.0], [160.0, 50.0],
		// 			[147.2, 49.8], [168.2, 49.2], [175.0, 73.2], [157.0, 47.8], [167.6, 68.8],
		// 			[159.5, 50.6], [175.0, 82.5], [166.8, 57.2], [176.5, 87.8], [170.2, 72.8],
		// 			[174.0, 54.5], [173.0, 59.8], [179.9, 67.3], [170.5, 67.8], [160.0, 47.0],
		// 			[154.4, 46.2], [162.0, 55.0], [176.5, 83.0], [160.0, 54.4], [152.0, 45.8],
		// 			[162.1, 53.6], [170.0, 73.2], [160.2, 52.1], [161.3, 67.9], [166.4, 56.6],
		// 			[168.9, 62.3], [163.8, 58.5], [167.6, 54.5], [160.0, 50.2], [161.3, 60.3],
		// 			[167.6, 58.3], [165.1, 56.2], [160.0, 50.2], [170.0, 72.9], [157.5, 59.8],
		// 			[167.6, 61.0], [160.7, 69.1], [163.2, 55.9], [152.4, 46.5], [157.5, 54.3],
		// 			[168.3, 54.8], [180.3, 60.7], [165.5, 60.0], [165.0, 62.0], [164.5, 60.3],
		// 			[156.0, 52.7], [160.0, 74.3], [163.0, 62.0], [165.7, 73.1], [161.0, 80.0],
		// 			[162.0, 54.7], [166.0, 53.2], [174.0, 75.7], [172.7, 61.1], [167.6, 55.7],
		// 			[151.1, 48.7], [164.5, 52.3], [163.5, 50.0], [152.0, 59.3], [169.0, 62.5],
		// 			[164.0, 55.7], [161.2, 54.8], [155.0, 45.9], [170.0, 70.6], [176.2, 67.2],
		// 			[170.0, 69.4], [162.5, 58.2], [170.3, 64.8], [164.1, 71.6], [169.5, 52.8],
		// 			[163.2, 59.8], [154.5, 49.0], [159.8, 50.0], [173.2, 69.2], [170.0, 55.9],
		// 			[161.4, 63.4], [169.0, 58.2], [166.2, 58.6], [159.4, 45.7], [162.5, 52.2],
		// 			[159.0, 48.6], [162.8, 57.8], [159.0, 55.6], [179.8, 66.8], [162.9, 59.4],
		// 			[161.0, 53.6], [151.1, 73.2], [168.2, 53.4], [168.9, 69.0], [173.2, 58.4],
		// 			[171.8, 56.2], [178.0, 70.6], [164.3, 59.8], [163.0, 72.0], [168.5, 65.2],
		// 			[166.8, 56.6], [172.7, 105.2], [163.5, 51.8], [169.4, 63.4], [167.8, 59.0],
		// 			[159.5, 47.6], [167.6, 63.0], [161.2, 55.2], [160.0, 45.0], [163.2, 54.0],
		// 			[162.2, 50.2], [161.3, 60.2], [149.5, 44.8], [157.5, 58.8], [163.2, 56.4],
		// 			[172.7, 62.0], [155.0, 49.2], [156.5, 67.2], [164.0, 53.8], [160.9, 54.4],
		// 			[162.8, 58.0], [167.0, 59.8], [160.0, 54.8], [160.0, 43.2], [168.9, 60.5],
		// 			[158.2, 46.4], [156.0, 64.4], [160.0, 48.8], [167.1, 62.2], [158.0, 55.5],
		// 			[167.6, 57.8], [156.0, 54.6], [162.1, 59.2], [173.4, 52.7], [159.8, 53.2],
		// 			[170.5, 64.5], [159.2, 51.8], [157.5, 56.0], [161.3, 63.6], [162.6, 63.2],
		// 			[160.0, 59.5], [168.9, 56.8], [165.1, 64.1], [162.6, 50.0], [165.1, 72.3],
		// 			[166.4, 55.0], [160.0, 55.9], [152.4, 60.4], [170.2, 69.1], [162.6, 84.5],
		// 			[170.2, 55.9], [158.8, 55.5], [172.7, 69.5], [167.6, 76.4], [162.6, 61.4],
		// 			[167.6, 65.9], [156.2, 58.6], [175.2, 66.8], [172.1, 56.6], [162.6, 58.6],
		// 			[160.0, 55.9], [165.1, 59.1], [182.9, 81.8], [166.4, 70.7], [165.1, 56.8],
		// 			[177.8, 60.0], [165.1, 58.2], [175.3, 72.7], [154.9, 54.1], [158.8, 49.1],
		// 			[172.7, 75.9], [168.9, 55.0], [161.3, 57.3], [167.6, 55.0], [165.1, 65.5],
		// 			[175.3, 65.5], [157.5, 48.6], [163.8, 58.6], [167.6, 63.6], [165.1, 55.2],
		// 			[165.1, 62.7], [168.9, 56.6], [162.6, 53.9], [164.5, 63.2], [176.5, 73.6],
		// 			[168.9, 62.0], [175.3, 63.6], [159.4, 53.2], [160.0, 53.4], [170.2, 55.0],
		// 			[162.6, 70.5], [167.6, 54.5], [162.6, 54.5], [160.7, 55.9], [160.0, 59.0],
		// 			[157.5, 63.6], [162.6, 54.5], [152.4, 47.3], [170.2, 67.7], [165.1, 80.9],
		// 			[172.7, 70.5], [165.1, 60.9], [170.2, 63.6], [170.2, 54.5], [170.2, 59.1],
		// 			[161.3, 70.5], [167.6, 52.7], [167.6, 62.7], [165.1, 86.3], [162.6, 66.4],
		// 			[152.4, 67.3], [168.9, 63.0], [170.2, 73.6], [175.2, 62.3], [175.2, 57.7],
		// 			[160.0, 55.4], [165.1, 104.1], [174.0, 55.5], [170.2, 77.3], [160.0, 80.5],
		// 			[167.6, 64.5], [167.6, 72.3], [167.6, 61.4], [154.9, 58.2], [162.6, 81.8],
		// 			[175.3, 63.6], [171.4, 53.4], [157.5, 54.5], [165.1, 53.6], [160.0, 60.0],
		// 			[174.0, 73.6], [162.6, 61.4], [174.0, 55.5], [162.6, 63.6], [161.3, 60.9],
		// 			[156.2, 60.0], [149.9, 46.8], [169.5, 57.3], [160.0, 64.1], [175.3, 63.6],
		// 			[169.5, 67.3], [160.0, 75.5], [172.7, 68.2], [162.6, 61.4], [157.5, 76.8],
		// 			[176.5, 71.8], [164.4, 55.5], [160.7, 48.6], [174.0, 66.4], [163.8, 67.3]]
		// 	},
		// 	{
		// 		name: 'Male',
		// 		color: 'rgba(119, 152, 191, .5)',
		// 		data: [[174.0, 65.6], [175.3, 71.8], [193.5, 80.7], [186.5, 72.6], [187.2, 78.8],
		// 		[181.5, 74.8], [184.0, 86.4], [184.5, 78.4], [175.0, 62.0], [184.0, 81.6],
		// 		[180.0, 76.6], [177.8, 83.6], [192.0, 90.0], [176.0, 74.6], [174.0, 71.0],
		// 		[184.0, 79.6], [192.7, 93.8], [171.5, 70.0], [173.0, 72.4], [176.0, 85.9],
		// 		[176.0, 78.8], [180.5, 77.8], [172.7, 66.2], [176.0, 86.4], [173.5, 81.8],
		// 		[178.0, 89.6], [180.3, 82.8], [180.3, 76.4], [164.5, 63.2], [173.0, 60.9],
		// 		[183.5, 74.8], [175.5, 70.0], [188.0, 72.4], [189.2, 84.1], [172.8, 69.1],
		// 		[170.0, 59.5], [182.0, 67.2], [170.0, 61.3], [177.8, 68.6], [184.2, 80.1],
		// 		[186.7, 87.8], [171.4, 84.7], [172.7, 73.4], [175.3, 72.1], [180.3, 82.6],
		// 		[182.9, 88.7], [188.0, 84.1], [177.2, 94.1], [172.1, 74.9], [167.0, 59.1],
		// 		[169.5, 75.6], [174.0, 86.2], [172.7, 75.3], [182.2, 87.1], [164.1, 55.2],
		// 		[163.0, 57.0], [171.5, 61.4], [184.2, 76.8], [174.0, 86.8], [174.0, 72.2],
		// 		[177.0, 71.6], [186.0, 84.8], [167.0, 68.2], [171.8, 66.1], [182.0, 72.0],
		// 		[167.0, 64.6], [177.8, 74.8], [164.5, 70.0], [192.0, 101.6], [175.5, 63.2],
		// 		[171.2, 79.1], [181.6, 78.9], [167.4, 67.7], [181.1, 66.0], [177.0, 68.2],
		// 		[174.5, 63.9], [177.5, 72.0], [170.5, 56.8], [182.4, 74.5], [197.1, 90.9],
		// 		[180.1, 93.0], [175.5, 80.9], [180.6, 72.7], [184.4, 68.0], [175.5, 70.9],
		// 		[180.6, 72.5], [177.0, 72.5], [177.1, 83.4], [181.6, 75.5], [176.5, 73.0],
		// 		[175.0, 70.2], [174.0, 73.4], [165.1, 70.5], [177.0, 68.9], [192.0, 102.3],
		// 		[176.5, 68.4], [169.4, 65.9], [182.1, 75.7], [179.8, 84.5], [175.3, 87.7],
		// 		[184.9, 86.4], [177.3, 73.2], [167.4, 53.9], [178.1, 72.0], [168.9, 55.5],
		// 		[157.2, 58.4], [180.3, 83.2], [170.2, 72.7], [177.8, 64.1], [172.7, 72.3],
		// 		[165.1, 65.0], [186.7, 86.4], [165.1, 65.0], [174.0, 88.6], [175.3, 84.1],
		// 		[185.4, 66.8], [177.8, 75.5], [180.3, 93.2], [180.3, 82.7], [177.8, 58.0],
		// 		[177.8, 79.5], [177.8, 78.6], [177.8, 71.8], [177.8, 116.4], [163.8, 72.2],
		// 		[188.0, 83.6], [198.1, 85.5], [175.3, 90.9], [166.4, 85.9], [190.5, 89.1],
		// 		[166.4, 75.0], [177.8, 77.7], [179.7, 86.4], [172.7, 90.9], [190.5, 73.6],
		// 		[185.4, 76.4], [168.9, 69.1], [167.6, 84.5], [175.3, 64.5], [170.2, 69.1],
		// 		[190.5, 108.6], [177.8, 86.4], [190.5, 80.9], [177.8, 87.7], [184.2, 94.5],
		// 		[176.5, 80.2], [177.8, 72.0], [180.3, 71.4], [171.4, 72.7], [172.7, 84.1],
		// 		[172.7, 76.8], [177.8, 63.6], [177.8, 80.9], [182.9, 80.9], [170.2, 85.5],
		// 		[167.6, 68.6], [175.3, 67.7], [165.1, 66.4], [185.4, 102.3], [181.6, 70.5],
		// 		[172.7, 95.9], [190.5, 84.1], [179.1, 87.3], [175.3, 71.8], [170.2, 65.9],
		// 		[193.0, 95.9], [171.4, 91.4], [177.8, 81.8], [177.8, 96.8], [167.6, 69.1],
		// 		[167.6, 82.7], [180.3, 75.5], [182.9, 79.5], [176.5, 73.6], [186.7, 91.8],
		// 		[188.0, 84.1], [188.0, 85.9], [177.8, 81.8], [174.0, 82.5], [177.8, 80.5],
		// 		[171.4, 70.0], [185.4, 81.8], [185.4, 84.1], [188.0, 90.5], [188.0, 91.4],
		// 		[182.9, 89.1], [176.5, 85.0], [175.3, 69.1], [175.3, 73.6], [188.0, 80.5],
		// 		[188.0, 82.7], [175.3, 86.4], [170.5, 67.7], [179.1, 92.7], [177.8, 93.6],
		// 		[175.3, 70.9], [182.9, 75.0], [170.8, 93.2], [188.0, 93.2], [180.3, 77.7],
		// 		[177.8, 61.4], [185.4, 94.1], [168.9, 75.0], [185.4, 83.6], [180.3, 85.5],
		// 		[174.0, 73.9], [167.6, 66.8], [182.9, 87.3], [160.0, 72.3], [180.3, 88.6],
		// 		[167.6, 75.5], [186.7, 101.4], [175.3, 91.1], [175.3, 67.3], [175.9, 77.7],
		// 		[175.3, 81.8], [179.1, 75.5], [181.6, 84.5], [177.8, 76.6], [182.9, 85.0],
		// 		[177.8, 102.5], [184.2, 77.3], [179.1, 71.8], [176.5, 87.9], [188.0, 94.3],
		// 		[174.0, 70.9], [167.6, 64.5], [170.2, 77.3], [167.6, 72.3], [188.0, 87.3],
		// 		[174.0, 80.0], [176.5, 82.3], [180.3, 73.6], [167.6, 74.1], [188.0, 85.9],
		// 		[180.3, 73.2], [167.6, 76.3], [183.0, 65.9], [183.0, 90.9], [179.1, 89.1],
		// 		[170.2, 62.3], [177.8, 82.7], [179.1, 79.1], [190.5, 98.2], [177.8, 84.1],
		// 		[180.3, 83.2], [180.3, 83.2]]
		// 	}
		// ];

		// Build series
		const seriesOptions =  [];
		// const seriesData = [];
		let sliceFound = false;

		try {

			// If series is Dynamic, update chartSeries
			let dynamicSeriesNames = [];
			if (chartSeriesDynamic) {
				// Create the various Dynamic Series Names
				dynamicSeriesNames = data.map(f => f[chartSeries[0].dynamicField])
					.filter((v, i, a) => a.indexOf(v) == i)
					.sort();

				// Create the the Dynamic Chart Series
				let dynamicSeries = [];
				for (let i = 0; i < dynamicSeriesNames.length; i++) {
					dynamicSeries.push({
						"name": dynamicSeriesNames[i],
						...chartSeries[0]
					});
				}
				console.log('DYNAMICSERIES: ', dynamicSeries);

				// Load dynamicSeries into chartSeries
				chartSeries = dynamicSeries;
			}
			console.log('CHARTSERIES: ', chartSeries);

			for (let i = 0; i < chartSeries.length; i++) {

				const seriesData = [];
				seriesData.push(
					data.map((d => {
						if (d[chartSeries[i].dynamicField] === chartSeries[i].name) {
							return [d[xValueField], d[yValueField] !== undefined ? d[yValueField] : null]
						}
					}))
					//.filter((v, i, a) => a.indexOf(v) == i)
					.sort()
				);
				console.log(seriesData);

				seriesOptions.push({
					name: chartSeries[i].name,
					color: chartSeries[i].color,
					data: seriesData
				});
			}
			console.log(' > SERIESOPTIONS: ', seriesOptions);


			// for (let d = 0; d < data.length; d++) {
			// 	const item = data[d];
			//
			// 	let value = item[categoryField];
			// 	if (!value)
			// 		value = "";
			// 	else
			// 		value = value.toString().toLowerCase();
			//
			// 	if (value === sliceValueToCompare) {
			// 		sliceFound = true;
			// 	}
			//
			// 	seriesData.push({
			// 		name: item[categoryField],
			// 		y: item[valueField],
			// 		value: item.value,
			// 		// color: getSeriesColor(d, value, sliceValueToCompare),
			// 		color: customColors !== undefined ? (sliceValueToCompare === '-2112' ? getCustomMatchDataColor(value, customColors) : getSeriesColor(d, value, sliceValueToCompare.toLowerCase())) : getSeriesColor(d, value, sliceValueToCompare.toLowerCase()),
			// 		sliced: value === sliceValueToCompare,
			// 		borderWidth:  value === sliceValueToCompare ? config.data.highlight.borderWidth : 0,
			// 		borderColor: value === sliceValueToCompare ? config.data.highlight.borderColor : 0
			// 	});
			// }
			// console.log(seriesData);

			// NOTE: If we don't find the active slice what do we want to do??
			// HACK: TEMP: Add the missing slice??
			// if (!sliceFound && sliceValueToCompare !== '-2112') {
			// 	seriesData.push({
			// 		name: sliceValue.toUpperCase(),
			// 		y: 0,
			// 		value: 0,
			// 		color: 'red',
			// 		sliced: true,
			// 		borderWidth:  config.data.highlight.borderWidth,
			// 		borderColor: config.data.highlight.borderColor,
			// 	});
			// 	setSliceValueNotFound(true);
			// } else {
			// 	setSliceValueNotFound(false);
			// }


			// Note: Data comes back as follows
			// scatterPlot: [
			//  {
			//      SiteId:,
			//      Readings: []
			//  },
			//  {
			//      SiteId:,
			//      Readings: []
			//  }
			// ]

			// Pull out into xReadings and yReadings
			// const xInfo = data.scatterPlot[0];
			// const yInfo = data.scatterPlot[1];
			//
			// const siteId = xInfo.SiteId;
			// const xReadingType = xInfo.ReadingType;
			// const yReadingType = yInfo.ReadingType;
			//
			// const xReadings = xInfo.Readings;
			// const yReadings = yInfo.Readings;
			//
			// const seriesData = [];
			// let thrownAway = 0;
			// // if (xReadings.length === yReadings.length) {
			// for (let x = 0; x < xReadings.length; x++) {
			// 	// NOTE: This is not supported with BOOST
			// 	// seriesData.push({x: xReadings[x].ReadingValue, y: yReadings[x].ReadingValue});
			//
			// 	// Find yReadings value
			// 	const readingTime = xReadings[x].ReadingTime;
			//
			// 	const subArrayToSearch = yReadings.slice((x - 20 >= 0 ? x - 20 : 0), x + 20 <= yReadings.length - 1 ? x + 20 : yReadings.length - 1);
			// 	const yValue = subArrayToSearch.find(r => r.ReadingTime === readingTime);
			//
			// 	if (yValue) {
			// 		seriesData.push([xReadings[x].ReadingValue, yValue.ReadingValue]);
			// 	} else {
			// 		thrownAway++;
			// 		// console.log('**WARN** Throwing {0} away... no match!'.format(readingTime));
			// 	}
			// }
			// // }
			//
			// if (thrownAway > 0) {
			// 	console.log('**WARN** Threw away {0} records bc times do not match... no match!'.format(thrownAway));
			// }

			// const seriesColor = addAlpha(getDataColor(0, siteId, app.colorMap), 0.5);
			// const seriesName = siteAttributes ? siteAttributes[nameField] ? siteAttributes[nameField] : siteId : siteId;

			// if (seriesData.length > 0) {
			// 	// seriesOptions.push({
			// 	// 	name: seriesName,
			// 	// 	color: seriesColor,
			// 	// 	data: seriesData
			// 	// });
			// }

		}
		catch(err) {
			setNoDataMessage("Chart Error");
			setChartData([]);
		}
		finally {
			if (!chart) {

				const chartOptions = {
					chart: {
						type: 'scatter',
						zoomType: 'xy',
						backgroundColor: null,
						resetZoomButton: {
							position: {
								// align: 'right', // by default
								// verticalAlign: 'top', // by default
								x: -30,
								//y: -30
							}
						}
					},
					tooltip: {
						// formatter: function() {
						// 	return ''+
						// 		this.x +' cm, '+ this.y +' kg';
						// }
						...(highchartOptions.tooltip ? highchartOptions.tooltip : {}),
						borderColor: "#000000",
						useHTML: true,
						followPointer: true,
						formatter: function () {
							let formattedTooltip = "<table>"
							formattedTooltip += formatTooltip(categoryFieldFormatters, true, t(this.series.name));
							formattedTooltip += (typeof this.x !== 'undefined') ? formatTooltip(null, false, this.x) : ''; // if undefined, assume there is no Value to show
							formattedTooltip += (typeof this.y !== 'undefined') ? formatTooltip(null, false, this.y) : ''; // if undefined, assume there is no Value to show
							formattedTooltip += "</table>"
							return formattedTooltip;
						}
					},
					navigation: {
						buttonOptions: {
							height: 20,
							width: 20,
							symbolSize: 10,
							symbolX: 11,
							symbolY: 10,
							symbolStrokeWidth: 1,
							y: 5,
							x: -2
						}
					},
					time: {
						useUTC: false
					},
					boost: {
						enabled: highchartOptions?.boost?.enabled !== undefined ? highchartOptions.boost.enabled : false,
						useGPUTranslations: highchartOptions?.boost?.useGPUTranslations !== undefined ? highchartOptions.boost.useGPUTranslations : true,
						usePreAllocated: highchartOptions?.boost?.usePreAllocated !== undefined ? highchartOptions.boost.usePreAllocated : true,
					},
					title: {
						text: ''
					},
					legend: {
						enabled: highchartOptions?.legend?.enabled !== undefined ? highchartOptions.legend.enabled : true,
						layout: 'horizontal',
						verticalAlign: 'bottom'
					},
					plotOptions: {
						// scatter: {
						// 	showInLegend: highchartOptions?.plotOptions?.scatter?.showInLegend !== undefined ? highchartOptions.plotOptions.scatter.showInLegend : false,
						// },
						series: {
							cursor: 'pointer',
							events: {
								click: function (event) {
									clickEvent(event);
									return false;
								}
							}
						}
					},
					credits: {
						enabled: false
					},
					xAxis: {
						title: {
							text: t(highchartOptions.xAxis.title.text) ? t(highchartOptions.xAxis.title.text) : ""
						},
						startOnTick: true,
						endOnTick: true,
						showLastLabel: true,
						// min: undefined,
						// max: undefined,
						// categories: ['test1','test2','test3','test4','test5','test6','test7','test8'] //yValueField === null ? seriesOptions.map(s => s.name.toString()).sort() : null
					},
					yAxis: {
						title: {
							text: t(highchartOptions.yAxis.title.text) ? t(highchartOptions.yAxis.title.text) : ""
						},
						// min: undefined,
						// max: undefined
					},
					series: seriesOptions
				}
				console.log('xAxis Categories: ', seriesOptions.map(s => s.name));
				console.log('ScatterChart: Create', chartOptions);

				setChart(Highcharts.chart(chartRef.current, chartOptions));
			} else {

				// console.log('PieChart: Update');

				chart.update({
					// NOTE: We need to update our handlers otherwise they will point to
					// a previously defined function with a context that does not match
					// the current state - to reproduce comment out plot options
					// filter once chart, clear, then filter the 2nd chart and all
					// previously applied filters will be displayed
					tooltip: {
						...(highchartOptions.tooltip ? highchartOptions.tooltip : {}),
						borderColor: "#000000",
						useHTML: true,
						followPointer: true,
						formatter: function () {
							let formattedTooltip = "<table>"
							formattedTooltip += formatTooltip(categoryFieldFormatters, true, t(this.series.name));
							formattedTooltip += (typeof this.x !== 'undefined') ? formatTooltip(null, false, this.x) : ''; // if undefined, assume there is no Value to show
							formattedTooltip += (typeof this.y !== 'undefined') ? formatTooltip(null, false, this.y) : ''; // if undefined, assume there is no Value to show
							formattedTooltip += "</table>"
							return formattedTooltip;
						}
					},
					plotOptions: {
						// scatter: {
						// 	showInLegend: highchartOptions?.plotOptions?.scatter?.showInLegend !== undefined ? highchartOptions.plotOptions.scatter.showInLegend : false,
						// },
						series: {
							cursor: 'pointer',
							events: {
								click: function (event) {
									clickEvent(event);
									return false;
								}
							}
						}
					},
					series: seriesOptions
				}, true, true);

			}
		}
	};

	const resizeCard = () => {

		const newCardHeaderStyle = Object.assign({}, {...styles?.cardHeader});
		const newCardContentStyle = Object.assign({}, {...styles?.cardContent
			,height: 'calc(100% - 49px)'
			,overflow: 'auto'
			,padding: '0px'}
		);

		if (title && subtitle) {
			// Default Style
		} else if (title) {
			// Title Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else if (subtitle) {
			// Subtitle Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else {
			// No title
			newCardHeaderStyle.height = '0px';
			newCardHeaderStyle.padding = '0px';
			newCardContentStyle.height = '100%';
		}
		setCardHeaderStyle(newCardHeaderStyle);
		setCardContentStyle(newCardContentStyle);
	}

	return (
		<StyledDiv>
			{showProgress && dataSource.fetching &&
				<LinearProgress sx={{height: '1px'}}/>
			}
			{showProgress && !dataSource.fetching &&
				<div sx={{height: '1px'}}></div>
			}
			<Card sx={{height: '100%', boxShadow: 'none', ...styles?.card}}>
				<StyledCardHeader action={ClearFilterBtn(id, false)} style={cardHeaderStyle}
							titleTypographyProps={{color: 'textPrimary', variant: 'subtitle2'}}
							title={t(title)}
							subheaderTypographyProps={{color: 'textSecondary', variant: 'body2'}}
							subheader={t(subtitle)}>
				</StyledCardHeader>
				<CardContent style={cardContentStyle}>
					<div id={"sccomp"} ref={chartRef} style={{position: 'relative', top: '0px', width: '100%', height: '100%'}}></div>
				</CardContent>
			</Card>
		</StyledDiv>
	);
}
